import { CalendarDays, Clock, Eye, Pencil, Trash2, User } from 'lucide-react';
import type { AdditionalTask } from '@pms/shared';
import { Card } from '@/components/ui/card';
import { fullName } from './taskConstants';

function minutesToLabel(mins: number): string {
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

interface AdditionalWorkCardProps {
  item: AdditionalTask;
  canManage?: boolean;
  onEdit?: (item: AdditionalTask) => void;
  onDelete?: (item: AdditionalTask) => void;
  onView?: (item: AdditionalTask) => void;
}

export function AdditionalWorkCard({ item, canManage, onEdit, onDelete, onView }: AdditionalWorkCardProps) {
  return (
    <Card variant="flat" className="group flex min-w-0 flex-col gap-3 overflow-hidden p-5 transition-colors hover:border-primary/40">
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 flex-1 break-words text-base font-semibold text-foreground">{item.title}</h3>
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          <Clock className="h-3.5 w-3.5" /> {minutesToLabel(item.duration_minutes)}
        </span>
      </div>

      {item.description && (
        <p className="line-clamp-2 break-words text-sm text-muted-foreground">{item.description}</p>
      )}

      <div className="mt-auto space-y-1.5 text-sm">
        <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
          <CalendarDays className="h-4 w-4 shrink-0" />
          <span className="truncate">{new Date(item.work_date).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
        <div className="flex min-w-0 items-center gap-2 text-foreground">
          <User className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate font-medium">{fullName(item.first_name, item.last_name)}</span>
          {item.employee_code && <span className="shrink-0 text-muted-foreground">({item.employee_code})</span>}
        </div>
        {item.assigned_by_first_name && (
          <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
            <User className="h-4 w-4 shrink-0" />
            <span className="min-w-0 truncate">Assigned by: <span className="font-medium text-foreground">{fullName(item.assigned_by_first_name, item.assigned_by_last_name)}</span></span>
          </div>
        )}
      </div>

      <div className="flex justify-end gap-1 border-t border-border/60 pt-2 opacity-0 transition-opacity group-hover:opacity-100">
        {onView && (
          <button type="button" onClick={() => onView(item)} title="View details"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            <Eye className="h-4 w-4" />
          </button>
        )}
        {canManage && onEdit && (
          <button type="button" onClick={() => onEdit(item)} title="Edit"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
            <Pencil className="h-4 w-4" />
          </button>
        )}
        {canManage && onDelete && (
          <button type="button" onClick={() => onDelete(item)} title="Delete"
            className="rounded-md p-1.5 text-muted-foreground hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </Card>
  );
}
